import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";
import PaginationForm from "../../../../shared/components/molecules/commons/pagination-form";
// Type definition for PDFDownloadLink render function
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9WBI.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rulw.ttf",
});
const zw = "\u200B";
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportRetirementTrendNext3YearsOTKByPositionListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ลำดับ",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่ง",
      field: "type",
      order: null,
      isOrder: true,
    },
    {
      label: "กรอบอัตรากำลัง",
      field: "allocated_power_rate",
      order: null,
      isOrder: true,
    },
    {
      label: "บรรจุจริง",
      field: "actual_filled",
      order: null,
      isOrder: true,
    },
    {
      label: "อัตราว่าง",
      field: "vacant_positions",
      order: null,
      isOrder: true,
    },
    {
      label: (moment().year() + 543).toString(),
      field: "retire_current",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 544),
      field: "retire_next1",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 545),
      field: "retire_next2",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKByPositionList(
            params
          );
        const resultAll =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKByPositionList(
            {
              ...params,
              page_size: 10000,
              page: 1,
            }
          );
        setData(result);
        setDataExport(resultAll);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering, queryParams.page_size, queryParams.page]
  );

  const tableRowNoData = () => {
    if ((data.rows?.position?.length ?? 0) === 0) {
      return <TableRowNoData colspan={8} />;
    }
    return null;
  };

  // ยอดรวมทั้งรายงาน (ไม่ผูกหน้า pagination) — ใช้ชุด export / total_all จาก backend
  const totalRow = (() => {
    const fromApi =
      dataExport?.rows?.total_all?.[0] || data?.rows?.total_all?.[0];
    if (fromApi) {
      return {
        allocated_power_rate: Number(fromApi.allocated_power_rate) || 0,
        actual_filled: Number(fromApi.actual_filled) || 0,
        vacant_positions: Number(fromApi.vacant_positions) || 0,
        retire_current: Number(fromApi.retire_current) || 0,
        retire_next1: Number(fromApi.retire_next1) || 0,
        retire_next2: Number(fromApi.retire_next2) || 0,
      };
    }
    const positions: any[] = dataExport?.rows?.position || [];
    if (!positions.length) return null;
    return positions.reduce(
      (acc, item) => ({
        allocated_power_rate:
          acc.allocated_power_rate + (Number(item.allocated_power_rate) || 0),
        actual_filled: acc.actual_filled + (Number(item.actual_filled) || 0),
        vacant_positions:
          acc.vacant_positions + (Number(item.vacant_positions) || 0),
        retire_current: acc.retire_current + (Number(item.retire_current) || 0),
        retire_next1: acc.retire_next1 + (Number(item.retire_next1) || 0),
        retire_next2: acc.retire_next2 + (Number(item.retire_next2) || 0),
      }),
      {
        allocated_power_rate: 0,
        actual_filled: 0,
        vacant_positions: 0,
        retire_current: 0,
        retire_next1: 0,
        retire_next2: 0,
      }
    );
  })();

  const onChangePageSize = (page_size: number) => {
    setQueryParams({
      ...queryParams,
      page_size: page_size,
    });
  };

  const onChangePage = (page: number) => {
    setQueryParams({
      ...queryParams,
      page: page,
    });
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 20, fontFamily: "kanit-regular" }}>
        

        {/* หัวรายงาน - หลัง fixed header, แสดงเฉพาะใน flow หน้าแรก แล้วตารางต่อทันที */}
        <View style={{ marginBottom: 20 }}>
          <Text
            style={{
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
              fontFamily: "kanit-regular",
            }}
          >
            {"องค์การตลาดเพื่อเกษตรกร" + zw}
          </Text>
          <Text
            style={{
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
              fontFamily: "kanit-regular",
            }}
          >
            {"รายงานแบ่งประเภท : สรุปแนวโน้มการเกษียณอายุของตำแหน่ง เป้าหมายในอีก 3 ปี ข้างหน้า" + zw}
          </Text>
          <Text
            style={{
              fontSize: 14,
              textAlign: "center",
              marginBottom: 1,
              fontFamily: "kanit-regular",
            }}
          >
            {"ประจำวันที่" + zw}{" "}
            {moment().format("D") +
              " " +
              thaiMonths[moment().month()] +
              " " +
              (moment().year() + 543) +
              zw}
          </Text>
        </View>

        {/* หัวตาราง - คอลัมน์ 1-5 รวม 2 แถว ข้อความอยู่ตรงกลาง, คอลัมน์ปีเกษียณมี 2 แถว แยกกัน */}
        <View
          fixed
          style={{
            flexDirection: "row",
            border: "1px solid black",
            fontFamily: "kanit-regular",
            fontSize: 11,
            backgroundColor: "white",
          }}
        >
          {/* คอลัมน์ 1-5: รวม 2 แถว ข้อความอยู่ตรงกลาง ไม่มีเส้นคั่นบนล่าง */}
          <View
            style={{
              width: "6%",
              justifyContent: "center",
              paddingVertical: 4,
              paddingHorizontal: 2,
              borderRight: "1px solid black",
            }}
          >
            <Text style={{ fontSize: 11, textAlign: "center" }}>
              {"ลำดับ" + zw}
            </Text>
          </View>
          <View
            style={{
              width: "36%",
              justifyContent: "center",
              paddingVertical: 4,
              paddingHorizontal: 2,
              borderRight: "1px solid black",
            }}
          >
            <Text style={{ fontSize: 11, textAlign: "center" }}>
              {"ตำแหน่ง" + zw}
            </Text>
          </View>
          <View
            style={{
              width: "11%",
              justifyContent: "center",
              paddingVertical: 4,
              paddingHorizontal: 2,
              borderRight: "1px solid black",
            }}
          >
            <Text style={{ fontSize: 11, textAlign: "center" }}>
              {"กรอบ" + zw} <br />
              {"อัตรากำลัง" + zw}
            </Text>
          </View>
          <View
            style={{
              width: "11%",
              justifyContent: "center",
              paddingVertical: 4,
              paddingHorizontal: 2,
              borderRight: "1px solid black",
            }}
          >
            <Text style={{ fontSize: 11, textAlign: "center" }}>
              {"บรรจุจริง" + zw}
            </Text>
          </View>
          <View
            style={{
              width: "11%",
              justifyContent: "center",
              paddingVertical: 4,
              paddingHorizontal: 2,
              borderRight: "1px solid black",
            }}
          >
            <Text style={{ fontSize: 11, textAlign: "center" }}>
              {"อัตราว่าง" + zw}
            </Text>
          </View>
          {/* คอลัมน์ปีเกษียณ: 2 แถว มีเส้นคั่นระหว่างกัน (27% = 9+9+9) */}
          <View style={{ width: "27%", flexDirection: "column" }}>
            <Text
              style={{
                fontSize: 11,
                textAlign: "center",
                paddingVertical: 4,
                paddingHorizontal: 2,
                borderBottom: "1px solid black",
              }}
            >
              {"ปีเกษียณ" + zw}
            </Text>
            <View
              style={{
                flexDirection: "row",
                fontFamily: "kanit-regular",
                fontSize: 11,
              }}
            >
              <Text
                style={{
                  width: "33.33%",
                  fontSize: 11,
                  textAlign: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                {(moment().year() + 543) + zw}
              </Text>
              <Text
                style={{
                  width: "33.33%",
                  fontSize: 11,
                  textAlign: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  borderRight: "1px solid black",
                }}
              >
                {(moment().year() + 544) + zw}
              </Text>
              <Text
                style={{
                  width: "33.34%",
                  fontSize: 11,
                  textAlign: "center",
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                }}
              >
                {(moment().year() + 545) + zw}
              </Text>
            </View>
          </View>
        </View>

        {/* ตัวตาราง - border อยู่ที่แต่ละ row เพื่อไม่ให้ container overflow ไปหน้าเปล่า */}
        <View>
          {data.rows?.position?.map((item: any, index: any) => {
            return (
              <View
                key={index}
                wrap={false}
                style={{
                  flexDirection: "row",
                  fontFamily: "kanit-200",
                  fontSize: 10,
                  borderLeft: "1px solid black",
                  borderRight: "1px solid black",
                  borderBottom: "1px solid black",
                }}
              >
                <Text
                  style={{
                    width: "6%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {index + 1 + zw}
                </Text>
                <Text
                  wrap
                  style={{
                    width: "36%",
                    fontSize: 9,
                    textAlign: "left",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.structure_position_name_level + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.allocated_power_rate + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.actual_filled + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.vacant_positions + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_current + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_next1 + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                  }}
                >
                  {item.retire_next2 + zw}
                </Text>
              </View>
            );
          })}

            {/* รวม */}
            {data.rows?.total_all?.map((item: any, index: any) => (
              <View
                key={index}
                wrap={false}
                style={{
                  flexDirection: "row",
                  fontFamily: "kanit-regular",
                  fontSize: 10,
                  borderLeft: "1px solid black",
                  borderRight: "1px solid black",
                  borderBottom: "1px solid black",
                }}
              >
                <Text
                  style={{
                    width: "6%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                ></Text>
                <Text
                  style={{
                    width: "36%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {"รวม" + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.allocated_power_rate + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.actual_filled + zw}
                </Text>
                <Text
                  style={{
                    width: "11%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.vacant_positions + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_current + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                    borderRight: "1px solid black",
                  }}
                >
                  {item.retire_next1 + zw}
                </Text>
                <Text
                  style={{
                    width: "9%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontWeight: "bold",
                  }}
                >
                  {item.retire_next2 + zw}
                </Text>
              </View>
            ))}
        </View>
      </Page>
    </Document>
  );

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData, queryParams]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
        รายงานแบ่งประเภท : สรุปแนวโน้มการเกษียณอายุของตำแหน่ง เป้าหมายในอีก 3 ปี ข้างหน้า
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        {/* <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div> */}
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานแบ่งประเภท : สรุปแนวโน้มการเกษียณอายุของตำแหน่ง เป้าหมายในอีก 3 ปี ข้างหน้า${queryParams.year}.pdf`}
          >
            {(({ loading }: any) => (
              <Button
                variant="primary"
                className="btn-action btn-mof-primary me-2"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                {loading ? "Loading..." : "Export"}
              </Button>
            )) as any}
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.position?.map((row: any, index: any) => {
              const pageOffset =
                ((queryParams.page || 1) - 1) * (queryParams.page_size || 10);
              return (
                <ReportRetirementTrendNext3YearsBYPositionRow
                  key={index}
                  {...row}
                  index={pageOffset + index + 1}
                  type={`${row.structure_position_name_level}`}
                />
              );
            })}
            {tableRowNoData()}
          </tbody>
          {totalRow && (
            <tfoot>
              <ReportRetirementTrendNext3YearsBYPositionRow
                index=""
                type="รวม"
                allocated_power_rate={totalRow.allocated_power_rate}
                actual_filled={totalRow.actual_filled}
                vacant_positions={totalRow.vacant_positions}
                retire_current={totalRow.retire_current}
                retire_next1={totalRow.retire_next1}
                retire_next2={totalRow.retire_next2}
                bold
              />
            </tfoot>
          )}
        </Table>
      </div>
      <div className="">
        <PaginationForm
          current_page={queryParams.page}
          count={data.count}
          page_size={queryParams.page_size}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};

const ReportRetirementTrendNext3YearsBYPositionRow: FC<any> = ({
  index,
  allocated_power_rate,
  actual_filled,
  vacant_positions,
  retire_current,
  retire_next1,
  retire_next2,
  type = null,
  bold = false,
}) => {
  const isTotal = type === "รวม" || bold;
  return (
    <tr
      style={
        isTotal
          ? { fontWeight: "bold", backgroundColor: "#f5f5f5" }
          : undefined
      }
    >
      <td>{isTotal ? "" : index}</td>
      <td>{type}</td>
      <td>{allocated_power_rate}</td>
      <td>{actual_filled}</td>
      <td>{vacant_positions}</td>
      <td>{retire_current}</td>
      <td>{retire_next1}</td>
      <td>{retire_next2}</td>
    </tr>
  );
};

export default ReportRetirementTrendNext3YearsOTKByPositionListPage;
